import { useCallback, useEffect, useState } from "react";

import type { CampsiteStoryblok } from "@/component-types-sb";
import { normalizeSlug } from "@/lib/utils/slug";

type CampsiteReference = CampsiteStoryblok | string | null | undefined;

const getCampsiteUuid = (campsite: CampsiteReference) =>
  typeof campsite === "string" ? campsite : campsite?.uuid;

const getCampsiteSlug = (campsite: CampsiteReference) =>
  typeof campsite === "string"
    ? null
    : normalizeSlug(campsite?.full_slug || campsite?.slug) || null;

export const useCampsiteOfferCounts = () => {
  const [campsites, setCampsites] = useState<CampsiteStoryblok[]>([]);

  useEffect(() => {
    let isActive = true;

    const fetchOfferCounts = async () => {
      try {
        const res = await fetch("/api/campsites?counts=offers");
        const json = await res.json();

        if (isActive) {
          setCampsites(Array.isArray(json) ? json : []);
        }
      } catch (error) {
        console.error(error);

        if (isActive) {
          setCampsites([]);
        }
      }
    };

    fetchOfferCounts();

    return () => {
      isActive = false;
    };
  }, []);

  const getCampsiteWithOfferCounts = useCallback(
    (current: CampsiteReference) => {
      const uuid = getCampsiteUuid(current);

      return uuid
        ? campsites.find((campsite) => campsite.uuid === uuid) || null
        : null;
    },
    [campsites]
  );

  const hasOffer = useCallback(
    (current: CampsiteReference) => {
      const campsite = getCampsiteWithOfferCounts(current);

      return campsite ? campsite.offers > 0 : false;
    },
    [getCampsiteWithOfferCounts]
  );

  const getOfferSlug = useCallback(
    (current: CampsiteReference) => {
      const campsite = getCampsiteWithOfferCounts(current);

      if (!campsite || campsite.offers <= 0) {
        return null;
      }

      return getCampsiteSlug(campsite) || getCampsiteSlug(current);
    },
    [getCampsiteWithOfferCounts]
  );

  return { getOfferSlug, hasOffer };
};
